import React, { Fragment } from "react";
import { lazyWithRetry } from "../../core/lazy_with_retry";

import { useDailyLocationGalleryFeatureFlag } from "../../core/splitio";
import { ImageData } from "../../data";
import { GeneralInformation } from "../template_offering_details_base";
import { OfferingDetailsDayPass } from "../template_offering_details_daypass";
import { Text } from "../text";
import { TextLink } from "../text_link";
import { useWeWorkAnalytics } from "./wework_analytics";
import { useWeWorkPartnerConfig } from "./wework_partner_config";

const Theme = lazyWithRetry(() => import("./wework_theme"));

export const faqsEN = [
  {
    question: "What does Daily Access give me permission to?",
    answer:
      "The Daily Access grants you entry to any selected WeWork location on the date you reserved. During your reservation, your access is granted during business hours (9 AM - 5 PM), Sunday – Thursday. Your open workspace booking will give you permission to work in the hot desk and/or common areas of the specific location. You’ll have access to a seat with a back, a flat work surface, and a power source within reach.",
  },

  {
    question:
      "How do I start using the Daily Access once arriving at the WeWork location?",
    answer:
      "When you first arrive at the building, please check-in with a member of our Community Team at the front desk. According to the building you reserved, you will get set up with a keycard – just don’t forget your ID, the Community Team will need to check that first!",
  },

  {
    question:
      "Anything specific I need to bring with me before arriving at WeWork?",
    answer:
      "Please ensure you have proof of identification, and any necessary equipment you need to make the most of your visit. Oh, and don’t forget a charger :)",
  },

  {
    question: "How do I modify or cancel my booking?",
    answer: (
      <Text>
        You may cancel or modify the date of your reservation 24 hours before
        your booking begins (until 9 AM the day before your reservation). In
        order to change your reservation, please reach out to
        <TextLink href="mailto:help@flexspace.ai">
          help@flexspace.ai
        </TextLink>{" "}
        24 hours before the reservation begins.
      </Text>
    ),
  },

  {
    question: "Why is the date I’m looking for not available?",
    answer:
      "In order to maximize the daily desks and WeWork members' experience, we have limited the daily capacity for daily/multi passess. Feel free to check out other WeWork buildings/dates.",
  },

  {
    question: "What is your guest policy for desk bookings?",
    answer: (
      <Text>
        Guests are not permitted with daily desk bookings. If you'd like to
        bring a guest, please{" "}
        <TextLink href="/offerings/wework-meeting-rooms">
          book a conference room
        </TextLink>{" "}
        that can accommodate each of your guests.
      </Text>
    ),
  },
  {
    question: "Do I have printer access?",
    answer: "Printing is not included in the daily desk membership.",
  },
];

export const faqsHE = [
  {
    question: "מה כוללת גישה יומית?",
    answer:
      "גישה יומית מעניקה לכם אפשרות להיכנס למיקום הנבחר של WeWork בתאריך ששריינתם. הגישה לחלל העבודה מתאפשרת בשעות הפעילות (09:00-17:00), בימים ראשון-חמישי. במסגרת הגישה היומית ניתן לעבוד מעמדות העבודה הפזורות בחללים המשותפים או באזורים משותפים אחרים במיקום שבחרתם.",
  },

  {
    question: "מה צריך לעשות כשמגיעים לבניין?",
    answer:
      "יש לבצע צ'ק אין כאשר אתם מגיעים לבניין, אנא גשו לצוות הקהילה שלנו בדלפק הכניסה בעת הגעתכם. לאחר מכן תקבלו כרטיס מפתח שיאפשר לכם גישה במהלך יום העבודה. רק אל תשכחו להביא איתכם תעודה מזהה, זה הדבר הראשון שצוות הקהילה יבקש מכם על מנת למסור לידכם כרטיס גישה.",
  },

  {
    question: "משהו ספציפי שצריך להביא לפני ההגעה ל-WeWork?",
    answer:
      "אנא וודאו שאתם מביאים איתכם תעודה מזהה, וכל ציוד אחר הדרוש לכם כדי להפיק את המירב מהביקור שלכם. אל תשכחו להביא מטען כמובן (:",
  },

  {
    question: "איך ניתן לשנות או לבטל את ההזמנה שלי?",
    answer: (
      <Text>
        ניתן לבטל או לשנות את ההזמנה עד 24 שעות לפני תחילת ההזמנה שלכם (עד השעה
        09:00 ביום שלפני ההזמנה שלכם). על מנת לבטל את ההזמנה יש לשלוח בקשה
        לכתובת האימייל{" "}
        <TextLink href="mailto:help@flexspace.ai">help@flexspace.ai</TextLink>{" "}
        עד 24 שעות לפני תחילתה כאמור.
      </Text>
    ),
  },

  {
    question: "מדוע תאריך מסוים אינו זמין?",
    answer:
      "כדי לספק את החווייה הטובה ביותר אנחנו מגבילים את כמות הגישות היומיות הניתנות ללוקיישן ספציפי. אנחנו מזמינים אתכם לבדוק בניינים או תאריכים אחרים במידה ולא קיימת זמינות.",
  },

  {
    question: "מה אומרת המדיניות שלכם בנוגע להבאת אורחים?",
    answer: (
      <Text>
        אין להביא אורחים כאשר מזמינים גישה יומית. אם ברצונכם להזמין אורחים,{" "}
        <TextLink href="/offerings/wework-meeting-rooms">
          אנא שריינו חדר ישיבות
        </TextLink>{" "}
        המתאים למספר האורחים שתרצו להזמין. יש להזין את שמות האורחים לפני תחילת
        ההזמנה והגעתם לבניין.{" "}
      </Text>
    ),
  },
  {
    question: "האם יש אפשרות להשתמש במדפסת?",
    answer: "הדפסות אינן כלולות ב׳גישה יומית׳.",
  },
];

const imagesDesktop: ImageData[] = [
  {
    url: "https://meet-production.s3.us-west-1.amazonaws.com/images/wework/access-desktop-1.jpg",
    width: 1200,
    height: 800,
  },
  {
    url: "https://meet-production.s3.us-west-1.amazonaws.com/images/wework/access-desktop-2.jpg",
    width: 1200,
    height: 800,
  },
  {
    url: "https://meet-production.s3.us-west-1.amazonaws.com/images/wework/access-desktop-3.jpg",
    width: 1200,
    height: 733,
  },
  {
    url: "https://meet-production.s3.us-west-1.amazonaws.com/images/wework/access-desktop-4.jpg",
    width: 1200,
    height: 800,
  },
  {
    url: "https://meet-production.s3.us-west-1.amazonaws.com/images/wework/access-desktop-5.jpg",
    width: 1200,
    height: 800,
  },
  {
    url: "https://meet-production.s3.us-west-1.amazonaws.com/images/wework/access-desktop-6.jpg",
    width: 1200,
    height: 800,
  },
  {
    url: "https://meet-production.s3.us-west-1.amazonaws.com/images/wework/access-desktop-7.jpg",
    width: 1200,
    height: 800,
  },
  {
    url: "https://meet-production.s3.us-west-1.amazonaws.com/images/wework/access-desktop-8.jpg",
    width: 1200,
    height: 800,
  },
];

const imagesMobile: ImageData[] = [
  {
    url: "https://meet-production.s3.us-west-1.amazonaws.com/images/wework/access-mobile-1.jpg",
    width: 640,
    height: 427,
  },
  {
    url: "https://meet-production.s3.us-west-1.amazonaws.com/images/wework/access-mobile-2.jpg",
    width: 640,
    height: 427,
  },
  {
    url: "https://meet-production.s3.us-west-1.amazonaws.com/images/wework/access-mobile-3.jpg",
    width: 640,
    height: 391,
  },
  {
    url: "https://meet-production.s3.us-west-1.amazonaws.com/images/wework/access-mobile-4.jpg",
    width: 640,
    height: 427,
  },
  {
    url: "https://meet-production.s3.us-west-1.amazonaws.com/images/wework/access-mobile-5.jpg",
    width: 640,
    height: 427,
  },
  {
    url: "https://meet-production.s3.us-west-1.amazonaws.com/images/wework/access-mobile-6.jpg",
    width: 640,
    height: 427,
  },
  {
    url: "https://meet-production.s3.us-west-1.amazonaws.com/images/wework/access-mobile-7.jpg",
    width: 640,
    height: 427,
  },
  {
    url: "https://meet-production.s3.us-west-1.amazonaws.com/images/wework/access-mobile-8.jpg",
    width: 640,
    height: 427,
  },
];

export const generalInfoEN: GeneralInformation = {
  title: "General information and cancellation policy",
  description:
    "Here are a few important details to note before booking your Daily Access:",
  bulletPoints: [
    {
      point: "Cancellation and changes",
      text: "You may cancel or change the date of your reservation 24 hours prior to your booking (until 9 AM the day before your reservation).",
    },
    {
      point: "Access hours",
      text: "Access to the building will be available between 9 AM to 5 PM Sunday - Thursday. Please note that it is not possible to access the building before 9 AM.",
    },
    {
      point: "Your work station",
      text: "The Daily Access allows you to work from our open shared spaces and/or lounges at the chosen building. The specific location in the building can be changed based on occupancy availability.",
    },
    {
      point: "Guest policy",
      text: (
        <Text>
          Guests are not allowed with your Daily Access booking. If you'd like
          to bring a guest, please{" "}
          <TextLink href="/offerings/wework-meeting-rooms">
            book a conference room
          </TextLink>{" "}
          that can accommodate the number of your guests. Guests' names must be
          entered before booking and arriving at the building.
        </Text>
      ),
    },
  ],
};

export const generalInfoHE: GeneralInformation = {
  title: "מידע כללי ומדיניות ביטולים",
  description: "כמה דברים שחשוב שתדעו לפני הזמנה של גישה יומית:",
  bulletPoints: [
    {
      point: "ביטולים ושינויים",
      text: "ניתן לשנות או לבטל את הזמנתכם עד 24 שעות לפני מועד ההזמנה (לכל המאוחר עד לשעה 09:00 בבוקר ביום שלפני מועד ההזמנה).",
    },
    {
      point: "שעות פעילות",
      text: "הגישה לבניין תתאפשר בין השעות 09:00 ל-17:00 בימים ראשון עד חמישי. כניסה לבניין לפני השעה 09:00 איננה אפשרית.",
    },
    {
      point: "עמדת העבודה שלכם",
      text: "גישה יומית מאפשרת לכם לעבוד מלאונג'ים וחללים פתוחים ומשותפים במיקום שבחרתם. אזורים ספציפיים בתוך הבניין עשויים להשתנות בהתאם לתפוסת החללים והזמינות שלהם.",
    },
    {
      point: "מדיניות אורחים",
      text: (
        <Text>
          אין להביא אורחים כאשר מזמינים גישה יומית. אם ברצונכם להזמין אורחים,{" "}
          <TextLink href="/offerings/wework-meeting-rooms">
            אנא שריינו חדר ישיבות
          </TextLink>{" "}
          המתאים למספר האורחים שתרצו להזמין. יש להזין את שמות האורחים לפני תחילת
          ההזמנה והגעתם לבניין.
        </Text>
      ),
    },
  ],
};

export const descriptionEN = `WeWork Daily Access provides you with a coworking space when you need it, without a long term commitment. Whether you’re tackling to-do’s or kicking off a project, you’ll have the space, community, and amenities to work towards your goals. Reserve your spot in advance, check-in, and get straight to work.`;
export const descriptionHE = `גישה יומית לחללי העבודה המשותפים במיקום ספציפי של WeWork, ללא התחייבות. בין אם אתם זקוקים לחלל עבור עבודה פרודקטיבית, או חלל מעורר השראה על מנת להניע פרויקט חדש, הגעתם למקום הנכון. אז איך זה עובד? מזמינים מקום מראש, מגיעים לצ'ק אין זריז ומתחילים לעבוד.`;

export function OfferingWeWorkDayPass() {
  useWeWorkAnalytics();
  const dailyLocationGalleryFeatureFlag = useDailyLocationGalleryFeatureFlag();
  const partnerConfig = useWeWorkPartnerConfig();
  return (
    <Fragment>
      <Theme />
      <OfferingDetailsDayPass
        includeQuantity={false}
        firstDayOfWeek={0}
        showBackButton={dailyLocationGalleryFeatureFlag}
        showAddress
        showDirections
        partnerConfig={partnerConfig}
        data={{
          description: descriptionEN,
          faqs: faqsEN,
          imagesMobile,
          imagesDesktop,
          generalInfo: generalInfoEN,
        }}
      />
    </Fragment>
  );
}
